import { Component } from "components/common";

/**
 * Eine Klasse zur Erstellung von HTML `<input>` Elementen.
 *
 * @example
 * Das folgende Beispiel erstellt ein HTML `<input>` Element.
 *
 * Erstelle ein neues HTML `<input>` Element.
 *
 * ```typescript
 * import { Input } from "components/common/input";
 *
 * const input = new Input();
 * ```
 *
 * Konfiguriere das HTML `<input>` Element durch Method Chaining.
 *
 * ```typescript
 * input
 *     .withID("input")
 *     .withClass("input");
 * ```
 *
 * Rendere nun das HTML `<input>` Element in das DOM.
 *
 * ```typescript
 * document.getElementById("root")
 *     ?.replaceChildren(input.render());
 * ```
 *
 * @public
 */
class Input extends Component<HTMLInputElement> {
  /**
   * Erstellt ein neues HTML `<input>` Element.
   */
  constructor() {
    super(document.createElement("input"));
  }

  /**
   * Fokussiert das HTML `<input>` Element.
   *
   * @returns Das fokussierte HTML `<input>` Element.
   *
   * @public
   */
  focus() {
    this.element.focus();

    return this;
  }

  /**
   * Fügt einen Typ hinzu.
   *
   * @param type - Ein Typ.
   * @returns Das HTML `<input>` Element mit dem Typ.
   *
   * @public
   */
  public withType(type: HTMLInputElement["type"]) {
    this.element.type = type;

    return this;
  }

  /**
   * Fügt einen Platzhalter hinzu.
   *
   * @param placeholder - Ein Platzhalter.
   * @returns Das HTML `<input>` Element mit dem Platzhalter.
   *
   * @public
   */
  public withPlaceholder(placeholder: HTMLInputElement["placeholder"]) {
    this.element.placeholder = placeholder;

    return this;
  }

  /**
   * Bestimmt, ob die Autovervollständigung aktiviert, oder deaktiviert wird.
   *
   * @param autocomplete - Ein Wert zur Aktivierung oder Deaktivierung der
   *                       Autovervollständigung.
   * @returns Das HTML `<input>` Element mit aktivierter oder deaktivierter
   *          Autovervollständigung.
   *
   * @public
   */
  public withAutocomplete(autocomplete: "on" | "off") {
    this.element.autocomplete = autocomplete;

    return this;
  }

  /**
   * Fügt einen Wert hinzu.
   *
   * @param value - Ein Wert.
   * @returns Das HTML `<input>` Element mit dem Wert.
   *
   * @public
   */
  public withValue(value: HTMLInputElement["value"]) {
    this.element.value = value;

    return this;
  }

  /**
   * Gibt den Wert vom HTML `<input>` Element zurück.
   *
   * @returns Der Wert vom HTML `<input>` Element.
   *
   * @public
   */
  public getValue() {
    return this.element.value;
  }
}

export { Input };
